export type AddonId = 'gps' | 'fuel' | 'fees';

export interface ProviderField {
  key: string;
  label: string;
  placeholder: string;
  type: 'url' | 'text' | 'password';
  required: boolean;
  hint?: string;
}

export interface AddonProvider {
  id: string;
  name: string;
  shortName: string;
  description: string;
  website: string;
  emoji: string;
  accentColor: string;
  fields: ProviderField[];
  testPath: string;
  endpoints: {
    vehicleLocation?: string;
    fuelLevel?: string;
    driverScore?: string;
    feeBalance?: string;
  };
  notes: string;
}

export interface AddonCategory {
  id: AddonId;
  label: string;
  icon: string;
  description: string;
}

export const ADDON_CATEGORIES: AddonCategory[] = [
  { id: 'gps',    label: 'GPS Tracking',        icon: '📍', description: 'Real-time vehicle positions pulled from your GPS provider and displayed on the live map.' },
  { id: 'fuel',   label: 'Fuel Monitoring',      icon: '⛽', description: 'Fuel level and refuel events from an onboard sensor — cross-checked against driver-reported fills.' },
  { id: 'fees',   label: 'School Fees Module',   icon: '📚', description: 'Fee balance lookups from the school\'s existing fees system — can flag unpaid families at boarding.' },
];

export const ADDON_PROVIDERS: Record<AddonId, AddonProvider[]> = {
  gps: [
    {
      id: 'karooooo',
      name: 'Karooooo (Cartrack Kenya)',
      shortName: 'Cartrack',
      description: "Kenya's largest GPS fleet tracking company — real-time positions, geofencing, and driver analytics. Has a real, documented Fleet REST API.",
      website: 'https://developer.cartrack.com',
      emoji: '🟠',
      accentColor: '#f97316',
      fields: [
        { key: 'apiUrl',   label: 'API Base URL', placeholder: 'https://fleetapi-ke.karooooo.com/rest', type: 'url',      required: true, hint: 'Kenya endpoint (karooooo.com/rest). Docs: developer.cartrack.com' },
        { key: 'apiKey',   label: 'Username',     placeholder: 'your Cartrack API username',             type: 'text',     required: true, hint: 'Cartrack Fleet API uses HTTP Basic Auth (username + password)' },
        { key: 'password', label: 'Password',     placeholder: '••••••••',                               type: 'password', required: true },
      ],
      testPath: '/vehicles/status',
      endpoints: { vehicleLocation: '/vehicles/status', fuelLevel: '/vehicles/status', driverScore: '/vehicles' },
      notes: 'Live position/odometer/fuel via /vehicles/status (~60 calls/min). When Fuel + GPS add-ons are both verified, PEAR uses sensor level as tank truth and hardware km for burn learning.',
    },
    {
      id: 'itrace',
      name: 'iTrace Africa',
      shortName: 'iTrace',
      description: 'Nairobi-based tracking company using Teltonika devices on the Wialon platform, covering cross-border East Africa fleets.',
      website: 'https://itraceafrica.itraceafrica.com',
      emoji: '🔵',
      accentColor: '#2563eb',
      fields: [
        { key: 'apiUrl',  label: 'Wialon Host URL', placeholder: 'https://hst-api.wialon.com/wialon/ajax.html', type: 'url',      required: true },
        { key: 'apiKey',  label: 'Wialon Token',    placeholder: 'xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',              type: 'password', required: true, hint: 'Generate in Wialon: User settings → Tokens → New token' },
        { key: 'unitId',  label: 'Unit / Group ID', placeholder: '12345678',                                     type: 'text',     required: false },
      ],
      testPath: '?svc=core/login&params={}',
      endpoints: { vehicleLocation: '?svc=core/search_items&params={spec:{type:avl_unit}}&unitId={unitId}' },
      notes: 'Use the Wialon Hosting API. Generate a long-lived token in Wialon Hosting and paste it above.',
    },
    {
      id: 'trailmycar',
      name: 'TrailMyCar',
      shortName: 'TrailMyCar',
      description: "Kenya's premier GPS tracking company with offices in Nairobi, Mombasa, Nakuru, and Eldoret.",
      website: 'https://www.trailmycar.com',
      emoji: '🟢',
      accentColor: '#16a34a',
      fields: [
        { key: 'apiUrl',         label: 'API Base URL',     placeholder: 'https://api.trailmycar.com/v2', type: 'url',      required: true },
        { key: 'apiKey',         label: 'API Key',          placeholder: 'tmc_key_xxxxxxxx',              type: 'password', required: true },
        { key: 'vehicleGroupId', label: 'Vehicle Group ID', placeholder: 'GRP-001',                      type: 'text',     required: false },
      ],
      testPath: '/status',
      endpoints: { vehicleLocation: '/vehicles/locations?group={vehicleGroupId}', fuelLevel: '/vehicles/fuel?group={vehicleGroupId}' },
      notes: 'Call TrailMyCar support to get API credentials. Not available on basic tracking plans.',
    },
    {
      id: 'rivercross',
      name: 'Rivercross Bluetrax',
      shortName: 'Bluetrax',
      description: 'Nairobi-based fleet management company with their own Bluetrax platform — vehicle tracking, cargo tracking, and fuel management.',
      website: 'https://rivercross.co.ke',
      emoji: '🔷',
      accentColor: '#0284c7',
      fields: [
        { key: 'apiUrl',   label: 'Bluetrax Host URL', placeholder: 'https://bluetrax.rivercross.co.ke/api', type: 'url',      required: true },
        { key: 'apiKey',   label: 'Username',          placeholder: 'your-username',                         type: 'text',     required: true },
        { key: 'password', label: 'Password',          placeholder: '••••••••',                              type: 'password', required: true },
      ],
      testPath: '/auth/ping',
      endpoints: { vehicleLocation: '/fleet/positions', fuelLevel: '/fleet/fuel' },
      notes: 'Use your Bluetrax portal login credentials. Contact Rivercross for API documentation.',
    },
    {
      id: 'naj_tracking',
      name: 'NAJ Tracking',
      shortName: 'NAJ',
      description: 'Local Kenyan tracking company offering GPS vehicle tracking and fuel management.',
      website: 'https://najtracking.co.ke',
      emoji: '🟡',
      accentColor: '#ca8a04',
      fields: [
        { key: 'apiUrl', label: 'API Base URL', placeholder: 'https://api.najtracking.co.ke/v1', type: 'url',      required: true },
        { key: 'apiKey', label: 'API Key',      placeholder: 'naj_xxxxxxxxxxxxxxxx',             type: 'password', required: true },
      ],
      testPath: '/ping',
      endpoints: { vehicleLocation: '/devices/locations', fuelLevel: '/devices/fuel' },
      notes: 'Request API access from NAJ Tracking when setting up your account.',
    },
    {
      id: 'tramigo',
      name: 'Tramigo Kenya',
      shortName: 'Tramigo',
      description: 'Long-running GPS vehicle tracking and fleet management provider operating across Kenya.',
      website: 'https://tramigo.com/ke-en/',
      emoji: '🟤',
      accentColor: '#92400e',
      fields: [
        { key: 'apiUrl', label: 'API Base URL', placeholder: 'https://api.tramigo.com/v1', type: 'url',      required: true },
        { key: 'apiKey', label: 'API Key',      placeholder: 'trg_xxxxxxxxxxxx',           type: 'password', required: true },
      ],
      testPath: '/status',
      endpoints: { vehicleLocation: '/vehicles/positions' },
      notes: 'Confirm API availability and credentials with Tramigo Kenya support.',
    },
    {
      id: 'fleetr',
      name: 'Fleetr',
      shortName: 'Fleetr',
      description: 'Affordable real-time vehicle tracking and theft prevention popular with smaller Kenyan fleets.',
      website: 'https://fleetr.co.ke/',
      emoji: '🟩',
      accentColor: '#15803d',
      fields: [
        { key: 'apiUrl', label: 'API Base URL', placeholder: 'https://api.fleetr.co.ke/v1', type: 'url',      required: true },
        { key: 'apiKey', label: 'API Key',      placeholder: 'flr_xxxxxxxxxxxx',            type: 'password', required: true },
      ],
      testPath: '/status',
      endpoints: { vehicleLocation: '/vehicles/locations' },
      notes: 'Confirm API access with Fleetr support — may require a business plan.',
    },
    {
      id: 'regent',
      name: 'Regent Tracking',
      shortName: 'Regent',
      description: 'Kenyan vehicle tracking & fleet management with real-time monitoring and security alerts.',
      website: 'https://regenttrack.co.ke/',
      emoji: '🟧',
      accentColor: '#ea580c',
      fields: [
        { key: 'apiUrl', label: 'API Base URL', placeholder: 'https://api.regenttrack.co.ke/v1', type: 'url',      required: true },
        { key: 'apiKey', label: 'API Key',      placeholder: 'rgt_xxxxxxxxxxxx',                 type: 'password', required: true },
      ],
      testPath: '/ping',
      endpoints: { vehicleLocation: '/fleet/positions' },
      notes: 'Request API documentation and credentials from Regent Tracking.',
    },
    {
      id: 'custom_gps',
      name: 'Custom / Other provider',
      shortName: 'Custom',
      description: 'Connect ANY tracker with a JSON REST endpoint — for vendors not in this list. You supply the URL, auth type, and endpoint path.',
      website: '',
      emoji: '⚙️',
      accentColor: '#475569',
      fields: [
        { key: 'apiUrl',    label: 'API Base URL',     placeholder: 'https://api.yourprovider.com', type: 'url',      required: true },
        { key: 'apiKey',    label: 'API Key / Username', placeholder: 'your key, token, or username', type: 'password', required: true },
        { key: 'authStyle', label: 'Auth type',        placeholder: 'bearer | header-key | basic | query-token', type: 'text', required: true, hint: 'How the key attaches: bearer header, x-api-key header, Basic (key=username, add a "password" below), or ?token=' },
        { key: 'password',  label: 'Password (only for Basic auth)', placeholder: '••••••••',         type: 'password', required: false },
        { key: 'dataPath',  label: 'Positions endpoint path', placeholder: '/vehicles/positions',     type: 'text',     required: true, hint: 'Appended to the base URL. Must return JSON with a vehicle list (plate + lat/lng or odometer).' },
      ],
      testPath: '',
      endpoints: {},
      notes: 'Advanced: the response should contain a list of vehicles with a number-plate field and either lat/lng or an odometer reading. Contact the technical team if you need help mapping unusual field names.',
    },
  ],

  fuel: [
    {
      id: 'karooooo_fuel',
      name: 'Karooooo (Cartrack Kenya)',
      shortName: 'Cartrack',
      description: 'Fuel monitoring on the Cartrack Fleet REST API — reads from the CAN bus or fuel sensor installed by Cartrack.',
      website: 'https://developer.cartrack.com',
      emoji: '🟠',
      accentColor: '#f97316',
      fields: [
        { key: 'apiUrl',   label: 'API Base URL', placeholder: 'https://fleetapi-ke.karooooo.com/rest', type: 'url',      required: true },
        { key: 'apiKey',   label: 'Username',     placeholder: 'your Cartrack API username',             type: 'text',     required: true, hint: 'Basic Auth — same credentials as Cartrack GPS if you use both' },
        { key: 'password', label: 'Password',     placeholder: '••••••••',                               type: 'password', required: true },
      ],
      testPath: '/vehicles/status',
      endpoints: { fuelLevel: '/vehicles/status' },
      notes: 'Same credentials as Cartrack GPS. Tank level becomes the Fuel Engine source of truth (math estimate only fills gaps when the sensor goes stale). Requires Cartrack fuel hardware.',
    },
    {
      id: 'trailmycar_fuel',
      name: 'TrailMyCar Fuel Monitoring',
      shortName: 'TrailMyCar',
      description: 'Dedicated fuel monitoring solution — tracks fuel levels and alerts for siphoning or rapid drops.',
      website: 'https://www.trailmycar.com/fuel-monitoring-solutions-in-kenya.html',
      emoji: '🟢',
      accentColor: '#16a34a',
      fields: [
        { key: 'apiUrl', label: 'API Base URL', placeholder: 'https://api.trailmycar.com/v2', type: 'url',      required: true },
        { key: 'apiKey', label: 'API Key',      placeholder: 'tmc_key_xxxxxxxx',              type: 'password', required: true },
      ],
      testPath: '/status',
      endpoints: { fuelLevel: '/vehicles/fuel' },
      notes: 'Fuel monitoring is a separate module from GPS tracking at TrailMyCar.',
    },
    {
      id: 'telematics_africa',
      name: 'Telematics Africa',
      shortName: 'Telematics Africa',
      description: 'Fuel monitoring specialist — handles buses, trucks, generators, and agricultural machinery.',
      website: 'https://telematicsafrica.com/fuel-monitoring-system-fleet-kenya',
      emoji: '⚙️',
      accentColor: '#7c3aed',
      fields: [
        { key: 'apiUrl',  label: 'API Base URL', placeholder: 'https://api.telematicsafrica.com/v1', type: 'url',      required: true },
        { key: 'apiKey',  label: 'API Key',      placeholder: 'ta_xxxxxxxxxxxxxxxx',                  type: 'password', required: true },
        { key: 'fleetId', label: 'Fleet ID',     placeholder: 'FL-0001',                              type: 'text',     required: false },
      ],
      testPath: '/health',
      endpoints: { fuelLevel: '/fleet/fuel?fleetId={fleetId}' },
      notes: 'Contact Telematics Africa for API documentation and credentials after hardware installation.',
    },
    {
      id: 'naj_fuel',
      name: 'NAJ Fuel Management',
      shortName: 'NAJ Fuel',
      description: 'NAJ Tracking\'s dedicated fuel management system — real-time consumption tracking and theft detection.',
      website: 'https://najtracking.co.ke/naj-fuel-management',
      emoji: '🟡',
      accentColor: '#ca8a04',
      fields: [
        { key: 'apiUrl',  label: 'API Base URL', placeholder: 'https://api.najtracking.co.ke/fuel/v1', type: 'url',      required: true },
        { key: 'apiKey',  label: 'API Key',      placeholder: 'naj_xxxxxxxxxxxxxxxx',                  type: 'password', required: true },
        { key: 'deviceId', label: 'Device ID',   placeholder: 'DEV-001',                               type: 'text',     required: false },
      ],
      testPath: '/ping',
      endpoints: { fuelLevel: '/devices/fuel?deviceId={deviceId}' },
      notes: 'Device ID is printed on the fuel sensor hardware NAJ installs.',
    },
    {
      id: 'controltech',
      name: 'Controltech EA',
      shortName: 'Controltech',
      description: 'East Africa fleet management — reported 25% fuel cost reduction for Nairobi logistics fleets.',
      website: 'https://www.controltech-ea.com',
      emoji: '🔴',
      accentColor: '#dc2626',
      fields: [
        { key: 'apiUrl',     label: 'API Base URL', placeholder: 'https://api.controltech-ea.com/v1', type: 'url',      required: true },
        { key: 'apiKey',     label: 'API Key',      placeholder: 'ctech_xxxxxxxxxxxxxxxx',            type: 'password', required: true },
        { key: 'accountId',  label: 'Account ID',   placeholder: 'ACC-0001',                          type: 'text',     required: false },
      ],
      testPath: '/status',
      endpoints: { fuelLevel: '/fleet/fuel?accountId={accountId}', vehicleLocation: '/fleet/positions?accountId={accountId}' },
      notes: 'Contact Controltech EA to enable API access — not available by default on all plans.',
    },
    {
      id: 'carro_fuel',
      name: 'Carro Tracking Solutions',
      shortName: 'Carro',
      description: 'Kenyan provider specialising in GPS tracking plus fuel and load monitoring.',
      website: 'https://www.cartrackingsolutionskenya.com/',
      emoji: '🟥',
      accentColor: '#b91c1c',
      fields: [
        { key: 'apiUrl', label: 'API Base URL', placeholder: 'https://api.carro.co.ke/v1', type: 'url',      required: true },
        { key: 'apiKey', label: 'API Key',      placeholder: 'carro_xxxxxxxxxxxx',          type: 'password', required: true },
      ],
      testPath: '/status',
      endpoints: { fuelLevel: '/vehicles/fuel' },
      notes: 'Confirm fuel-monitoring API access with Carro Tracking Solutions.',
    },
    {
      id: 'tramigo_fuel',
      name: 'Tramigo Fuel',
      shortName: 'Tramigo',
      description: 'Fuel monitoring as part of Tramigo Kenya’s fleet management platform.',
      website: 'https://tramigo.com/ke-en/',
      emoji: '🟤',
      accentColor: '#92400e',
      fields: [
        { key: 'apiUrl', label: 'API Base URL', placeholder: 'https://api.tramigo.com/v1', type: 'url',      required: true },
        { key: 'apiKey', label: 'API Key',      placeholder: 'trg_xxxxxxxxxxxx',           type: 'password', required: true },
      ],
      testPath: '/status',
      endpoints: { fuelLevel: '/vehicles/fuel' },
      notes: 'Confirm fuel-monitoring API availability with Tramigo Kenya.',
    },
    {
      id: 'custom_fuel',
      name: 'Custom / Other provider',
      shortName: 'Custom',
      description: 'Connect ANY fuel sensor system with a JSON REST endpoint. You supply the URL, auth type, and endpoint path.',
      website: '',
      emoji: '⚙️',
      accentColor: '#475569',
      fields: [
        { key: 'apiUrl',    label: 'API Base URL',      placeholder: 'https://api.yourprovider.com', type: 'url',      required: true },
        { key: 'apiKey',    label: 'API Key / Username', placeholder: 'your key, token, or username', type: 'password', required: true },
        { key: 'authStyle', label: 'Auth type',         placeholder: 'bearer | header-key | basic | query-token', type: 'text', required: true, hint: 'How the key attaches to requests' },
        { key: 'password',  label: 'Password (only for Basic auth)', placeholder: '••••••••',          type: 'password', required: false },
        { key: 'dataPath',  label: 'Fuel endpoint path', placeholder: '/vehicles/fuel',               type: 'text',     required: true, hint: 'Must return JSON with a vehicle list (plate + fuel litres).' },
      ],
      testPath: '',
      endpoints: {},
      notes: 'Advanced: the response should list vehicles with a number-plate field and a fuel-litres field. Contact the technical team for help mapping unusual field names.',
    },
  ],

  fees: [
    {
      id: 'zeraki',
      name: 'Zeraki Finance',
      shortName: 'Zeraki',
      description: 'Most widely used school fees platform in Kenya — 4,000+ schools, M-Pesa reconciliation, parent app.',
      website: 'https://www.zeraki.app',
      emoji: '🟣',
      accentColor: '#7c3aed',
      fields: [
        { key: 'apiUrl',    label: 'API Base URL', placeholder: 'https://api.zeraki.app/v1',    type: 'url',      required: true },
        { key: 'apiKey',    label: 'API Key',      placeholder: 'zrk_live_xxxxxxxxxxxxxxxx',    type: 'password', required: true, hint: 'Found in Zeraki Admin → Settings → API Access' },
        { key: 'schoolId',  label: 'School ID',    placeholder: 'SCH-0001',                     type: 'text',     required: true,  hint: 'Your institution ID in the Zeraki portal' },
      ],
      testPath: '/health',
      endpoints: { feeBalance: '/finance/students/{studentId}/balance?schoolId={schoolId}' },
      notes: 'Zeraki Finance API access is available on the Pro plan. Contact support@zeraki.app to enable it for your school.',
    },
    {
      id: 'ebingwa',
      name: 'EBingwa',
      shortName: 'EBingwa',
      description: 'CBC-ready school management platform with automated M-Pesa fee reconciliation and parent communication.',
      website: 'https://ebingwa.co.ke',
      emoji: '🔵',
      accentColor: '#2563eb',
      fields: [
        { key: 'apiUrl',        label: 'API Base URL',    placeholder: 'https://api.ebingwa.co.ke/v1', type: 'url',      required: true },
        { key: 'apiKey',        label: 'API Key',         placeholder: 'ebg_xxxxxxxxxxxxxxxx',          type: 'password', required: true },
        { key: 'institutionId', label: 'Institution ID',  placeholder: 'INST-001',                      type: 'text',     required: true },
      ],
      testPath: '/status',
      endpoints: { feeBalance: '/fees/balance?studentId={studentId}&institutionId={institutionId}' },
      notes: 'Request API credentials from EBingwa support. Specify that you need the fee balance endpoint for third-party integration.',
    },
    {
      id: 'jibusms',
      name: 'JibuSMS',
      shortName: 'JibuSMS',
      description: 'Full school ERP covering academics, fees, exams, and communications — widely used in Kenyan primary schools.',
      website: 'https://www.jibusms.com',
      emoji: '🟢',
      accentColor: '#16a34a',
      fields: [
        { key: 'apiUrl',     label: 'API Base URL', placeholder: 'https://api.jibusms.com/v1', type: 'url',      required: true },
        { key: 'apiKey',     label: 'API Token',    placeholder: 'jbs_xxxxxxxxxxxxxxxx',        type: 'password', required: true },
        { key: 'schoolCode', label: 'School Code',  placeholder: 'JBSSC-001',                  type: 'text',     required: true },
      ],
      testPath: '/ping',
      endpoints: { feeBalance: '/fees/student/{studentId}?schoolCode={schoolCode}' },
      notes: 'JibuSMS API is available on the Business tier. Contact JibuSMS support with your school code to enable.',
    },
    {
      id: 'smartsoma',
      name: 'SmartSoma',
      shortName: 'SmartSoma',
      description: 'Multi-branch school platform — administration, teacher and parent portals, fee tracking across campuses.',
      website: 'https://smartsoma.co.ke',
      emoji: '🟡',
      accentColor: '#ca8a04',
      fields: [
        { key: 'apiUrl',    label: 'API Base URL', placeholder: 'https://api.smartsoma.co.ke/v1', type: 'url',      required: true },
        { key: 'apiKey',    label: 'API Key',      placeholder: 'ss_live_xxxxxxxxxxxxxxxx',         type: 'password', required: true },
        { key: 'branchId',  label: 'Branch ID',    placeholder: 'BR-001',                            type: 'text',     required: false },
      ],
      testPath: '/health',
      endpoints: { feeBalance: '/finances/students/{studentId}/fees?branchId={branchId}' },
      notes: 'Use your SmartSoma admin portal to generate an API key under Settings → Integrations.',
    },
    {
      id: 'zyno',
      name: 'ZYNO EduVibe (Elite Mindz)',
      shortName: 'ZYNO',
      description: 'Cloud-based School ERP with M-Pesa integration and AI-driven analytics — tailored for Kenyan CBC curriculum.',
      website: 'https://elitemindz.co',
      emoji: '🔴',
      accentColor: '#dc2626',
      fields: [
        { key: 'apiUrl',    label: 'API Base URL', placeholder: 'https://api.elitemindz.co/zyno/v1', type: 'url',      required: true },
        { key: 'apiKey',    label: 'API Key',      placeholder: 'zyno_xxxxxxxxxxxxxxxx',              type: 'password', required: true },
        { key: 'schoolId',  label: 'School ID',    placeholder: 'SCH-0001',                           type: 'text',     required: true },
      ],
      testPath: '/status',
      endpoints: { feeBalance: '/fees/check?studentId={studentId}&schoolId={schoolId}' },
      notes: 'Contact Elite Mindz to enable the trasse integration add-on on your ZYNO EduVibe account.',
    },
    {
      id: 'elimikasasa',
      name: 'Elimikasasa',
      shortName: 'Elimikasasa',
      description: 'Long-running Kenyan cloud school management system (CBC + 8-4-4) with Lipa na M-Pesa fee collection.',
      website: 'https://elimikasasa.co.ke/',
      emoji: '🟩',
      accentColor: '#15803d',
      fields: [
        { key: 'apiUrl',   label: 'API Base URL', placeholder: 'https://api.elimikasasa.co.ke/v1', type: 'url',      required: true },
        { key: 'apiKey',   label: 'API Key',      placeholder: 'elm_xxxxxxxxxxxx',                 type: 'password', required: true },
        { key: 'schoolId', label: 'School ID',    placeholder: 'SCH-0001',                          type: 'text',     required: true },
      ],
      testPath: '/status',
      endpoints: { feeBalance: '/fees/balances?schoolId={schoolId}' },
      notes: 'Confirm fee-balance API access with Elimikasasa support.',
    },
    {
      id: 'elitek',
      name: 'EliTek',
      shortName: 'EliTek',
      description: 'Kenyan education management system with student records, attendance, and M-Pesa fee integration.',
      website: 'https://itkenya.com/erp-software-kenya/school-management-system/',
      emoji: '🔵',
      accentColor: '#2563eb',
      fields: [
        { key: 'apiUrl',   label: 'API Base URL', placeholder: 'https://api.elitek.co.ke/v1', type: 'url',      required: true },
        { key: 'apiKey',   label: 'API Key',      placeholder: 'elt_xxxxxxxxxxxx',            type: 'password', required: true },
        { key: 'schoolId', label: 'School ID',    placeholder: 'SCH-0001',                     type: 'text',     required: true },
      ],
      testPath: '/status',
      endpoints: { feeBalance: '/fees/balances?schoolId={schoolId}' },
      notes: 'Confirm fee-balance API access with EliTek support.',
    },
    {
      id: 'schule',
      name: 'SCHULE',
      shortName: 'SCHULE',
      description: 'Full school suite — student management, automated M-Pesa fee collection, academic records and payroll.',
      website: 'https://schoolsnearme.co.ke/',
      emoji: '🟡',
      accentColor: '#ca8a04',
      fields: [
        { key: 'apiUrl',   label: 'API Base URL', placeholder: 'https://api.schule.co.ke/v1', type: 'url',      required: true },
        { key: 'apiKey',   label: 'API Key',      placeholder: 'sch_xxxxxxxxxxxx',            type: 'password', required: true },
        { key: 'schoolId', label: 'School ID',    placeholder: 'SCH-0001',                     type: 'text',     required: true },
      ],
      testPath: '/status',
      endpoints: { feeBalance: '/fees/balances?schoolId={schoolId}' },
      notes: 'Confirm fee-balance API access with SCHULE support.',
    },
    {
      id: 'custom_fees',
      name: 'Custom / Other system',
      shortName: 'Custom',
      description: 'Connect ANY school system with a JSON REST endpoint that returns fee balances. You supply the URL, auth type, and endpoint path.',
      website: '',
      emoji: '⚙️',
      accentColor: '#475569',
      fields: [
        { key: 'apiUrl',    label: 'API Base URL',      placeholder: 'https://api.yourschoolsystem.com', type: 'url',      required: true },
        { key: 'apiKey',    label: 'API Key / Username', placeholder: 'your key, token, or username',     type: 'password', required: true },
        { key: 'authStyle', label: 'Auth type',         placeholder: 'bearer | header-key | basic | query-token', type: 'text', required: true, hint: 'How the key attaches to requests' },
        { key: 'password',  label: 'Password (only for Basic auth)', placeholder: '••••••••',              type: 'password', required: false },
        { key: 'dataPath',  label: 'Fee-balances endpoint path', placeholder: '/fees/balances',           type: 'text',     required: true, hint: 'Must return JSON with student records (parent email/name + paid status / balance).' },
      ],
      testPath: '',
      endpoints: {},
      notes: 'Advanced: the response should list students with a parent email or name and a paid flag or balance. Contact the technical team for help mapping unusual field names.',
    },
  ],
};

export function getProviders(addonId: AddonId): AddonProvider[] {
  return ADDON_PROVIDERS[addonId] || [];
}

export function getProvider(addonId: AddonId, providerId: string): AddonProvider | undefined {
  return ADDON_PROVIDERS[addonId]?.find((p) => p.id === providerId);
}

export function getCategoryMeta(addonId: AddonId): AddonCategory | undefined {
  return ADDON_CATEGORIES.find((c) => c.id === addonId);
}
